import { DefectStatus } from '../../models/defect.model';
import { buildSavedViewFiltersJson, parseSavedViewFilters } from '../../shared/saved-views/saved-view-filters';
import { DEFECT_STATUSES } from './defect-status';

/** Inhalt von `SavedView.filtersJson` im Kontext `DEFECTS`; die Suche gehört nicht zur Ansicht. */
export interface DefectSavedViewFilters {
  status: DefectStatus | null;
  releaseId: string | null;
  /** `none` = nicht zugewiesen, sonst die Nutzer-ID. */
  assignee: string | null;
}

/** Werte der drei Auswahlen der Seite; `''` heißt „Alle“. */
export interface DefectPageFilters {
  status: string;
  release: string;
  assignee: string;
}

export interface DefectSavedViewLabels {
  status: (status: DefectStatus) => string;
  release: (id: string) => string;
  person: (id: string) => string | null;
  unassigned: string;
  all: string;
}

export const UNASSIGNED_FILTER = '__none';
const UNASSIGNED_VIEW = 'none';

const NONE: DefectSavedViewFilters = { status: null, releaseId: null, assignee: null };

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value ? value : null;
}

export function parseDefectSavedViewFilters(filtersJson: string | null): DefectSavedViewFilters {
  const parsed = parseSavedViewFilters<Record<keyof DefectSavedViewFilters, unknown>>(filtersJson, NONE);
  return {
    status: DEFECT_STATUSES.find((known) => known === parsed.status) ?? null,
    releaseId: stringOrNull(parsed.releaseId),
    assignee: stringOrNull(parsed.assignee),
  };
}

export function buildDefectSavedViewFiltersJson(filters: DefectSavedViewFilters): string {
  return buildSavedViewFiltersJson({
    status: filters.status,
    releaseId: filters.releaseId,
    assignee: filters.assignee,
  });
}

/** Ein nicht mehr vorhandenes Release oder eine nicht mehr vorhandene Person gilt als „Alle“. */
export function knownDefectSavedViewFilters(
  filters: DefectSavedViewFilters,
  known: { releaseIds: readonly string[]; assigneeIds: readonly string[] },
): DefectSavedViewFilters {
  const assignee = filters.assignee;
  return {
    status: filters.status,
    releaseId: filters.releaseId && known.releaseIds.includes(filters.releaseId) ? filters.releaseId : null,
    assignee: assignee === UNASSIGNED_VIEW || (assignee && known.assigneeIds.includes(assignee)) ? assignee : null,
  };
}

export function defectViewFiltersFromPage(page: DefectPageFilters): DefectSavedViewFilters {
  return {
    status: (page.status as DefectStatus) || null,
    releaseId: page.release || null,
    assignee: page.assignee === UNASSIGNED_FILTER ? UNASSIGNED_VIEW : page.assignee || null,
  };
}

export function defectPageFiltersFromView(view: DefectSavedViewFilters): DefectPageFilters {
  return {
    status: view.status ?? '',
    release: view.releaseId ?? '',
    assignee: view.assignee === UNASSIGNED_VIEW ? UNASSIGNED_FILTER : (view.assignee ?? ''),
  };
}

export function isDefectSavedViewDirty(saved: DefectSavedViewFilters, current: DefectSavedViewFilters): boolean {
  return (
    saved.status !== current.status || saved.releaseId !== current.releaseId || saved.assignee !== current.assignee
  );
}

export function summarizeDefectSavedView(filters: DefectSavedViewFilters, labels: DefectSavedViewLabels): string {
  const assignee = filters.assignee;
  const parts = [
    filters.status ? labels.status(filters.status) : null,
    filters.releaseId ? labels.release(filters.releaseId) : null,
    assignee === UNASSIGNED_VIEW ? labels.unassigned : assignee ? labels.person(assignee) : null,
  ].filter((part): part is string => !!part);
  return parts.length ? parts.join(' · ') : labels.all;
}
